import { z } from 'zod';
import { ticketSourceSchema } from './ticket';

/**
 * Screenshot attachment metadata — auto-captured client-side at capture time
 * (browserMeta.ts / consoleBuffer.ts in the web app) and persisted on the
 * TicketAttachment row as JSON. Every field is optional: capture runs across a
 * range of browsers where any given API (userAgentData, devicePixelRatio) may
 * be absent, so the schema tolerates partial metadata rather than rejecting it.
 */
export const consoleErrorEntrySchema = z
  .object({
    level: z.enum(['error', 'warn', 'unhandledrejection']),
    // Ring-buffer entries are truncated client-side; cap defensively here too.
    message: z.string().max(2000),
    // Epoch millis or ISO string — tolerated either way (cross-lane contract).
    ts: z.union([z.number(), z.string()]).optional(),
  })
  .strict();
export type ConsoleErrorEntry = z.infer<typeof consoleErrorEntrySchema>;

export const attachmentMetaSchema = z
  .object({
    browser: z.string().max(120).optional(),
    browserVersion: z.string().max(60).optional(),
    os: z.string().max(120).optional(),
    screenW: z.number().int().nonnegative().optional(),
    screenH: z.number().int().nonnegative().optional(),
    viewportW: z.number().int().nonnegative().optional(),
    viewportH: z.number().int().nonnegative().optional(),
    dpr: z.number().positive().optional(),
    timezone: z.string().max(120).optional(),
    capturedAt: z.string().datetime().optional(),
    consoleErrors: z.array(consoleErrorEntrySchema).max(50).optional(),
  })
  .strict();
export type AttachmentMeta = z.infer<typeof attachmentMetaSchema>;

/**
 * Comment + metadata for an attachment. Image bytes travel as a raw request
 * body (image/*), NOT in this JSON payload — this schema validates the
 * second step of the two-step upload (PATCH .../attachments/:attachmentId).
 */
export const ticketAttachmentCreateSchema = z
  .object({
    comment: z.string().max(2000, 'Comment must be 2000 characters or fewer').optional(),
    meta: attachmentMetaSchema.optional(),
  })
  .strict();
export type TicketAttachmentCreateInput = z.infer<typeof ticketAttachmentCreateSchema>;

export const ticketAttachmentSummarySchema = z.object({
  id: z.string(),
  ticketId: z.string(),
  uploadedById: z.string().nullable(),
  comment: z.string().nullable(),
  imageCategory: z.string(),
  imageKey: z.string(),
  // Stored path GET /api/files/:category/:key serves from.
  imagePath: z.string(),
  meta: attachmentMetaSchema.nullable(),
  createdAt: z.string().datetime(),
});
export type TicketAttachmentSummary = z.infer<typeof ticketAttachmentSummarySchema>;

/**
 * Create a comment on a ticket. `isInternal` marks an ADMIN/SUPPORT-only note;
 * the service rejects it for CLIENT callers (internal notes are never exposed
 * to a client at either the service or route layer).
 */
export const ticketCommentCreateSchema = z
  .object({
    body: z
      .string()
      .trim()
      .min(1, 'Comment body is required')
      .max(20000, 'Comment must be 20000 characters or fewer'),
    isInternal: z.boolean().optional(),
  })
  .strict();
export type TicketCommentCreateInput = z.infer<typeof ticketCommentCreateSchema>;

export const ticketCommentSummarySchema = z.object({
  id: z.string(),
  ticketId: z.string(),
  authorId: z.string().nullable(),
  body: z.string(),
  isInternal: z.boolean(),
  source: ticketSourceSchema,
  createdAt: z.string().datetime(),
});
export type TicketCommentSummary = z.infer<typeof ticketCommentSummarySchema>;
